import TIM from 'tim-js-sdk'
import TIMUploadPlugin from 'tim-upload-plugin'

export default {
  namespaced: true,
  state: {
    timInstance: null,
    conversations: [],
    conversationHistory: {},
    currentConversationId: null,
    isCustomerService: false,
    initialized: false,
    imStatus: 'disconnected' // 添加IM状态: disconnected/connecting/connected
  },
  mutations: {
    SET_TIM_INSTANCE(state, instance) {
      state.timInstance = instance
    },
    SET_CONVERSATIONS(state, conversations) {
      state.conversations = conversations
    },
    ADD_CONVERSATION(state, conversation) {
      const exists = state.conversations.some(c => c.conversationID === conversation.conversationID)
      if (!exists) {
        state.conversations.push(conversation)
      }
    },
    ADD_MESSAGE_TO_HISTORY(state, { conversationID, message }) {
      if (!state.conversationHistory[conversationID]) {
        state.conversationHistory[conversationID] = []
      }
      state.conversationHistory[conversationID].push(message)
    },
    SET_CURRENT_CONVERSATION(state, conversationID) {
      state.currentConversationId = conversationID
    },
    CLEAR_CONVERSATIONS(state) {
      state.conversations = []
      state.conversationHistory = {}
      state.currentConversationId = null
    },
    MARK_AS_READ(state, conversationID) {
      const conversation = state.conversations.find(c => c.conversationID === conversationID)
      if (conversation) {
        conversation.unreadCount = 0
      }
    },
    SET_CUSTOMER_SERVICE_STATUS(state, status) {
      state.isCustomerService = status
    },
    SET_INITIALIZED(state, value) {
      state.initialized = value
    },
    SET_IM_STATUS(state, status) {
      state.imStatus = status
    }
  },
  actions: {
    // 初始化IM并登录
    async loginIM({ state, commit, dispatch, rootState }) { // 添加 state 参数
      // 关键检测：如果未登录则直接返回
      if (!rootState.user || !rootState.user.userCode) {
        console.warn('IM登录中止: 用户未登录')
        commit('SET_IM_STATUS', 'disconnected')
        return
      }
      try {
        commit('SET_IM_STATUS', 'connecting')

        const userInfo = rootState.user
        if (!userInfo || !userInfo.userCode) {
          console.warn('无法登录IM: 缺少用户标识')
          commit('SET_IM_STATUS', 'disconnected')
          return
        }

        // 如果已有实例，先登出
        if (state.timInstance) {
          await state.timInstance.logout()
          commit('SET_TIM_INSTANCE', null)
        }

        // 创建TIM实例
        const tim = TIM.create({
          SDKAppID: 1400539979
        })

        // 注册插件
        tim.registerPlugin({ 'tim-upload-plugin': TIMUploadPlugin })

        // 设置日志级别
        tim.setLogLevel(1)

        // 生成UserSig - 生产环境中应从后端API获取
        const userSig = rootState.app.generateUserSig(userInfo.userCode)

        // 登录IM
        await tim.login({
          userID: userInfo.userCode,
          userSig,
          useWebSocket: true
        })

        commit('SET_TIM_INSTANCE', tim)
        commit('SET_IM_STATUS', 'connected')

        // 初始化消息监听
        dispatch('initMessageListeners', tim)

        // 如果是客服角色，初始化客服系统
        if (rootState.user.userRole === 'code001') {
          commit('SET_CUSTOMER_SERVICE_STATUS', true)
          await dispatch('initCustomerSystem', tim)
        } else {
          commit('SET_CUSTOMER_SERVICE_STATUS', false)
        }

        console.log('IM登录成功')
      } catch (error) {
        console.error('IM登录失败', error)
        commit('SET_IM_STATUS', 'disconnected')
        // 注意：在 Vuex 中不能直接使用 this.$notify
        // 错误通知应该在组件中处理
      }
    },

    // 初始化消息监听
    initMessageListeners({ commit, dispatch }, tim) {
      // 监听新消息
      tim.on(TIM.EVENT.MESSAGE_RECEIVED, event => {
        dispatch('handleNewMessage', event.data)
      })

      // 监听会话列表更新
      tim.on(TIM.EVENT.CONVERSATION_LIST_UPDATED, event => {
        commit('SET_CONVERSATIONS', event.data)
      })

      // 监听被踢出事件
      tim.on(TIM.EVENT.KICKED_OUT, event => {
        console.warn('IM连接被踢出:', event)
        commit('SET_IM_STATUS', 'disconnected')
        // 错误通知应该在组件中处理
      })

      // 监听SDK准备就绪
      tim.on(TIM.EVENT.SDK_READY, () => {
        commit('SET_IM_STATUS', 'ready')
        console.log('SDK准备就绪')
      })

      // 监听网络状态变化
      tim.on(TIM.EVENT.NET_STATE_CHANGE, (event) => {
        console.log('网络状态变化:', event.data.state)
        if (event.data.state === TIM.TYPES.NET_STATE_CONNECTED) {
          commit('SET_IM_STATUS', 'connected')
        } else {
          commit('SET_IM_STATUS', 'connecting')
        }
      })
    },

    // 初始化客服系统
    async initCustomerSystem({ state, commit, dispatch }, tim) { // 添加 state 参数
      if (state.initialized) return

      try {
        // 获取会话列表
        const res = await tim.getConversationList()
        commit('SET_CONVERSATIONS', res.data.conversationList || [])

        commit('SET_INITIALIZED', true)
        console.log('客服系统初始化成功')
      } catch (error) {
        console.error('客服系统初始化失败', error)
      }
    },

    // 处理新消息
    handleNewMessage({ commit, state }, messages) {
      messages.forEach(message => {
        const conversationID = message.conversationID

        // 保存消息
        commit('ADD_MESSAGE_TO_HISTORY', {
          conversationID,
          message
        })

        // 如果当前不是活跃会话，增加未读数
        if (state.currentConversationId !== conversationID) {
          const conversation = state.conversations.find(c => c.conversationID === conversationID)
          if (conversation) {
            conversation.unreadCount = (conversation.unreadCount || 0) + 1
          }
        }
      })
    },

    // 选择会话
    async selectConversation({ commit, state }, conversationID) {
      commit('SET_CURRENT_CONVERSATION', conversationID)
      commit('MARK_AS_READ', conversationID)

      // 设置消息已读
      if (state.timInstance) {
        await state.timInstance.setMessageRead({ conversationID })
      }
    },

    // 发送消息
    async sendMessage({ state, commit, rootState }, { conversationID, messageContent }) {
      if (!state.timInstance || !conversationID || !messageContent) {
        throw new Error('IM未初始化或参数缺失')
      }

      if (state.imStatus !== 'ready') {
        throw new Error('IM尚未准备就绪')
      }

      try {
        // 解析对方用户ID（从会话ID中）
        const userId = conversationID.startsWith('C2C') ? conversationID.substring(3) : conversationID

        // 创建文本消息
        const message = state.timInstance.createTextMessage({
          to: userId,
          conversationType: TIM.TYPES.CONV_C2C,
          payload: { text: messageContent }
        })

        // 发送消息
        await state.timInstance.sendMessage(message)

        // 添加到本地历史
        commit('ADD_MESSAGE_TO_HISTORY', {
          conversationID,
          message: {
            ...message,
            from: rootState.user.userCode,
            time: Date.now()
          }
        })

        return true
      } catch (error) {
        console.error('发送消息失败', error)
        throw error
      }
    },

    // 发送通话信令
    async sendCallSignal({ state }, { to, payload }) {
      if (!state.timInstance) {
        throw new Error('IM未初始化')
      }

      if (state.imStatus !== 'ready') {
        throw new Error('IM尚未准备就绪')
      }

      try {
        // 创建自定义消息
        const message = state.timInstance.createCustomMessage({
          to,
          conversationType: TIM.TYPES.CONV_C2C,
          payload: {
            data: JSON.stringify(payload),
            description: '视频通话信令'
          }
        })

        // 发送消息
        await state.timInstance.sendMessage(message)
        return true
      } catch (error) {
        console.error('发送通话信令失败', error)
        throw error
      }
    },

    // 登出IM
    logoutIM({ state, commit }) { // 添加 state 参数
      if (state.timInstance) {
        state.timInstance.logout()
        state.timInstance.destroy()
      }
      commit('CLEAR_CONVERSATIONS')
      commit('SET_TIM_INSTANCE', null)
      commit('SET_CUSTOMER_SERVICE_STATUS', false)
      commit('SET_INITIALIZED', false)
      commit('SET_IM_STATUS', 'disconnected')
    },

    // 清除IM数据
    clear({ commit }) {
      commit('CLEAR_CONVERSATIONS')
      commit('SET_TIM_INSTANCE', null)
      commit('SET_CUSTOMER_SERVICE_STATUS', false)
      commit('SET_INITIALIZED', false)
      commit('SET_IM_STATUS', 'disconnected')
    },

    // 重连IM
    async reconnectIM({ dispatch, state }) {
      if (state.imStatus === 'connected') return

      try {
        await dispatch('loginIM')
      } catch (error) {
        console.error('IM重连失败', error)
        setTimeout(() => dispatch('reconnectIM'), 10000)
      }
    }
  },
  getters: {
    // 当前会话详细信息
    currentConversation: state => {
      return state.conversations.find(c => c.conversationID === state.currentConversationId)
    },

    // 当前会话的消息
    currentMessages: state => {
      if (!state.currentConversationId) return []
      return state.conversationHistory[state.currentConversationId] || []
    },

    // 是否是客服
    isCustomerService: state => state.isCustomerService,

    // IM连接状态
    imStatus: state => state.imStatus
  }
}
